import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, AlertTitle, Box, Button, CircularProgress, ListItemIcon, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import SocialApi from '../api/social';
import { CONNECTIONS_CHANGED } from '../hooks/useConnectionIssues';
import { accountTitle } from '../components/connections/AccountDetailsDialog';
import ConnectionsTable from '../components/connections/ConnectionsTable';
import { connectionHealth } from '../components/connections/health';
import AppCard from '../components/connections/AppCard';
import { DEFAULT_SITE, siteMeta } from '../components/connections/sites';

export default function Connections() {
  const [searchParams, setSearchParams] = useSearchParams();
  // one entry per app (SocialClientCredential) with its connections
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  // the login in progress: { site, appId, connectionId, account }; connectionId is set when reconnecting one account
  const [pending, setPending] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  // site filter; falls back to DEFAULT_SITE, then the first configured site
  const [siteFilter, setSiteFilter] = useState(DEFAULT_SITE);
  // the app card clicked to narrow the table to its connections; null shows all the site's
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [message, setMessage] = useState(null);
  const callbackHandled = useRef(false);

  const loadApps = useCallback(async () => {
    try {
      const data = await SocialApi.apps();
      setApps(data.results);
      window.dispatchEvent(new Event(CONNECTIONS_CHANGED));
    } catch (e) {
      setMessage({ severity: 'error', text: e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  // The site's login redirects back here with ?site=<site>&code=...&state=... (or ?error=...)
  useEffect(() => {
    const site = searchParams.get('site');
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    if (!site || (!code && !error) || callbackHandled.current) {
      loadApps();
      return;
    }
    callbackHandled.current = true;
    setSearchParams({}, { replace: true });
    setSiteFilter(site);
    const started = takePendingLogin();
    setPending(started?.site === site ? started : { site, appId: null, connectionId: null });
    SocialApi.connect(site, {
      code, error, state: searchParams.get('state'), error_message: searchParams.get('error_description'),
    })
      .then((data) => {
        setMessage(loginResult(data, started));
        setHighlightId(data.connection_id);
      })
      .catch((e) => setMessage({ severity: 'error', text: e.message }))
      .finally(() => {
        setPending(null);
        loadApps();
      });
  }, [searchParams, setSearchParams, loadApps]);

  // the highlight on the added or updated account fades after a few seconds
  useEffect(() => {
    if (!highlightId) return undefined;
    const timer = setTimeout(() => setHighlightId(null), 4000);
    return () => clearTimeout(timer);
  }, [highlightId]);

  // logs in through app; connection is the account being reconnected, null adds an account
  const login = async (app, connection = null) => {
    const started = {
      site: app.site, appId: app.id, connectionId: connection?.id ?? null,
      account: connection && accountName(connection),
    };
    setPending(started);
    setMessage(null);
    try {
      const { results } = await SocialApi.redirectUrl(app.site, app.id);
      savePendingLogin(started);
      window.location.assign(results);
    } catch (e) {
      setMessage({ severity: 'error', text: e.message });
      setPending(null);
    }
  };

  // the site filter's entries: each site once, with all its apps' connections
  const sites = useMemo(() => Object.values(apps.reduce((bySite, app) => {
    const entry = bySite[app.site] ?? { site: app.site, label: app.label, connections: [] };
    entry.connections = entry.connections.concat(app.connections);
    return { ...bySite, [app.site]: entry };
  }, {})), [apps]);
  const site = sites.find((item) => item.site === siteFilter) ?? sites.find((item) => item.site === DEFAULT_SITE)
    ?? sites[0];
  const meta = site && siteMeta(site);
  const siteApps = site ? apps.filter((app) => app.site === site.site) : [];
  const selectedApp = siteApps.find((app) => app.id === selectedAppId);
  // each connection's app, for its reconnect
  const appOf = Object.fromEntries(siteApps.flatMap((app) => app.connections
    .map((connection) => [connection.id, app])));
  const tableConnections = (selectedApp ? [selectedApp] : siteApps).flatMap((app) => app.connections);
  const chooseSite = (value) => {
    setSiteFilter(value);
    setSelectedAppId(null);
  };

  return (
    <Box component="section">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-end' }}
             justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>Connections</Typography>
          <Typography color="text.secondary">
            Link your ad platform accounts so their campaigns and insights can be synced.
          </Typography>
        </Box>
        {sites.length > 0 && (
          <SiteFilter sites={sites} value={site.site} onChange={chooseSite} />
        )}
      </Stack>

      <BrokenConnectionsBanner apps={apps} selected={site?.site} disabled={pending !== null}
                               onView={chooseSite} onReconnect={(item) => login(item.app, item.connection)} />

      {message && (
        <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2.5 }}>{message.text}</Alert>
      )}

      {loading ? (
        <CircularProgress size={24} />
      ) : sites.length === 0 ? (
        <Alert severity="info">
          No apps are configured yet. Load them with <code>manage.py load_social_clients &lt;site&gt;</code>.
        </Alert>
      ) : (
        <>
          {/* the site's apps, in the order they were added, two per row */}
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
            {siteApps.map((app) => (
              <AppCard key={app.id} app={app} meta={meta} selected={app.id === selectedAppId}
                       adding={pending?.appId === app.id && !pending.connectionId} disabled={pending !== null}
                       onSelect={() => setSelectedAppId(app.id === selectedAppId ? null : app.id)}
                       onLogin={() => login(app)} />
            ))}
          </Box>
          {(meta.description || meta.loginHint) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {[meta.description, meta.loginHint].filter(Boolean).join(' ')}
            </Typography>
          )}

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 4, mb: 1.5 }}>
            <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
              {selectedApp ? `Accounts connected through ${selectedApp.app_name || selectedApp.app_id}`
                : 'Connected accounts'}
            </Typography>
            {selectedApp && <Button size="small" onClick={() => setSelectedAppId(null)}>Show all</Button>}
          </Stack>
          <ConnectionsTable connections={tableConnections} meta={meta} pendingId={pending?.connectionId}
                            highlightId={highlightId} disabled={pending !== null} showApp={siteApps.length > 1}
                            canReconnect={(connection) => appOf[connection.id]?.is_active !== false}
                            onReconnect={(connection) => login(appOf[connection.id], connection)} />
        </>
      )}
    </Box>
  );
}

/**
 * Red banner while any connection is not working: names them, and offers Reconnect for a single one (through an
 * active app) or View for another site's
 */
function BrokenConnectionsBanner({ apps, selected, disabled, onView, onReconnect }) {
  const broken = apps.flatMap((app) => app.connections
    .filter((connection) => connectionHealth(connection).level === 'error')
    .map((connection) => ({ app, site: app.site, name: siteMeta(app).name, connection })));
  if (broken.length === 0) return null;

  const shown = broken.slice(0, 3);
  const otherSite = broken.find((item) => item.site !== selected);
  let action = null;
  if (broken.length === 1 && broken[0].app.is_active) {
    action = (
      <Button color="inherit" size="small" variant="outlined" disabled={disabled}
              onClick={() => onReconnect(broken[0])}>
        Reconnect
      </Button>
    );
  } else if (otherSite) {
    action = <Button color="inherit" size="small" onClick={() => onView(otherSite.site)}>View</Button>;
  }

  return (
    <Alert severity="error" variant="filled" action={action} sx={{ mb: 2.5, alignItems: 'center' }}>
      <AlertTitle sx={{ mb: 0.25 }}>
        {broken.length === 1 ? '1 connection is not working' : `${broken.length} connections are not working`}
      </AlertTitle>
      Their ad accounts stop syncing until they are reconnected:{' '}
      {shown.map((item, index) => (
        <span key={item.connection.id}>
          {index > 0 && ', '}
          <strong>{accountTitle(item.connection)}</strong> ({item.app.app_name || item.name}
          {item.connection.error_message ? ` – ${item.connection.error_message}` : ''})
        </span>
      ))}
      {broken.length > shown.length && ` and ${broken.length - shown.length} more`}.
    </Alert>
  );
}

function SiteFilter({ sites, value, onChange }) {
  return (
    <TextField select size="small" label="Site" value={value} onChange={(event) => onChange(event.target.value)}
               sx={{ minWidth: 220, bgcolor: 'background.paper' }}
               SelectProps={{ renderValue: (selected) => siteMeta(sites.find((item) => item.site === selected)).name }}>
      {sites.map((item) => {
        const { name, icon: Icon, color } = siteMeta(item);
        return (
          <MenuItem key={item.site} value={item.site}>
            {Icon && <ListItemIcon sx={{ color }}><Icon fontSize="small" /></ListItemIcon>}
            {name}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 'auto', pl: 2 }}>
              {item.connections.length}
            </Typography>
          </MenuItem>
        );
      })}
    </TextField>
  );
}

// The login survives the round trip to the site in sessionStorage; it is only a hint for the result message
const PENDING_LOGIN_KEY = 'connections.pendingLogin';

function savePendingLogin(started) {
  try {
    sessionStorage.setItem(PENDING_LOGIN_KEY, JSON.stringify(started));
  } catch {
    // storage blocked: the result message is just less specific
  }
}

function takePendingLogin() {
  try {
    const value = sessionStorage.getItem(PENDING_LOGIN_KEY);
    sessionStorage.removeItem(PENDING_LOGIN_KEY);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

// Reconnecting one account while the site was signed in as another user adds that user instead
function loginResult(data, started) {
  if (data.created && started?.connectionId && started.connectionId !== data.connection_id) {
    return {
      severity: 'warning',
      text: `${data.message} Facebook was signed in as a different user than ${started.account}, so it was added `
        + `as a separate account. To reconnect ${started.account}, sign in to Facebook as that user first.`,
    };
  }
  return { severity: 'success', text: data.message };
}

function accountName(connection) {
  return connection.name || connection.user_email || 'this account';
}
